import { appealsApi } from '../appealsApi';
import { AppealLetterView, AppealStatus } from '../types/appealTypes';
import {
  GenerateAppealLetterRequest,
  SaveAppealLetterRequest,
  UpdateAppealStatusRequest,
} from '../types/requestTypes';

const appealEndpoints = appealsApi.injectEndpoints({
  endpoints: (builder) => ({
    // Generate a new appeal letter using AI
    // POST /appeal/generate
    generateAppealLetter: builder.mutation<AppealLetterView, GenerateAppealLetterRequest>({
      query: (body) => ({
        url: 'appeal/generate',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AppealList'],
    }),

    // Get a single appeal letter by ID
    // GET /appeal?id=<id>
    getAppealLetter: builder.query<AppealLetterView, string>({
      query: (id) => ({
        url: 'appeal',
        params: { id },
      }),
      providesTags: (result, error, id) => [{ type: 'Appeal', id }],
    }),

    // Save/Update an appeal letter
    // POST /appeal
    saveAppealLetter: builder.mutation<AppealLetterView, SaveAppealLetterRequest>({
      query: (body) => ({
        url: 'appeal',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: any[] = ['AppealList'];
        if (result?.id) {
          tags.push({ type: 'Appeal', id: result.id });
        }
        if (arg.id) {
          tags.push({ type: 'Appeal', id: arg.id });
        }
        return tags;
      },
    }),

    // Update appeal status
    // PATCH /appeal/status
    updateAppealStatus: builder.mutation<AppealLetterView, UpdateAppealStatusRequest>({
      query: (body) => ({
        url: 'appeal/status',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Appeal', id: arg.id }, 'AppealList'],
    }),

    // List all appeals for an encounter
    // GET /appeals?encounterId=<encounterId>&status=<status>
    listAppealsByEncounter: builder.query<
      AppealLetterView[],
      { encounterId: string; status?: AppealStatus }
    >({
      query: ({ encounterId, status }) => ({
        url: 'appeals',
        params: { encounterId, status },
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Appeal' as const, id })), 'AppealList']
          : ['AppealList'],
    }),

    // List all appeals for an account
    // GET /appeals/byaccount?accountId=<accountId>&status=<status>
    listAppealsByAccount: builder.query<
      AppealLetterView[],
      { accountId: string; status?: AppealStatus }
    >({
      query: ({ accountId, status }) => ({
        url: 'appeals/byaccount',
        params: { accountId, status },
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Appeal' as const, id })), 'AppealList']
          : ['AppealList'],
    }),

    // Export appeal letter as PDF
    // POST /appeal/export
    exportAppealPdf: builder.mutation<
      { pdfUrl: string },
      { id: string; includeAttachments?: boolean }
    >({
      query: ({ id, includeAttachments }) => ({
        url: 'appeal/export',
        method: 'POST',
        body: { id, includeAttachments },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Appeal', id: arg.id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateAppealLetterMutation,
  useGetAppealLetterQuery,
  useSaveAppealLetterMutation,
  useUpdateAppealStatusMutation,
  useListAppealsByEncounterQuery,
  useListAppealsByAccountQuery,
  useExportAppealPdfMutation,
} = appealEndpoints;
